import { type InfiniteData, type QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { IPagedCollection } from '../../extViewmodels';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { ISupportThread } from '../../models/Support/ISupportThread';
import { ISupportTicket } from '../../models/Support/ISupportTicket';
import {
	SUPPORT_PATHS,
	SUPPORT_POLL_INTERVAL_MS,
	SUPPORT_QUERY_KEYS,
	supportPollingOptions,
} from './supportQueryHelpers';

interface SupportTicketQueryOptions {
	/** Override the default polling interval (in ms). Pass `false` to disable polling. */
	refetchInterval?: number | false;
}

type SupportTicketListCacheData =
	| IPagedCollection<ISupportTicket>
	| InfiniteData<IPagedCollection<ISupportTicket>, string>;

/** Finds the selected ticket in any cached inbox list so detail panes can render without a blank loading flash. */
const getCachedTicketFromLists = (queryClient: QueryClient, ticketId: string): ISupportTicket | undefined => {
	if (!ticketId) return undefined;

	const listQueries = queryClient.getQueriesData<SupportTicketListCacheData>({
		queryKey: SUPPORT_QUERY_KEYS.tickets.lists(),
	});
	for (const [, listData] of listQueries) {
		if (!listData) continue;
		const pages = 'pages' in listData ? listData.pages : [listData];
		for (const page of pages) {
			const ticket = page.values.find(candidate => candidate.id === ticketId);
			if (ticket) return ticket;
		}
	}

	return undefined;
};

export function useSupportTicketQuery(ticketId: string, options: SupportTicketQueryOptions = {}) {
	const { refetchInterval = SUPPORT_POLL_INTERVAL_MS } = options;
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useQuery({
		queryKey: SUPPORT_QUERY_KEYS.tickets.detail(ticketId),
		staleTime: 5_000,
		placeholderData: () => getCachedTicketFromLists(queryClient, ticketId),
		...supportPollingOptions(refetchInterval),
		queryFn: () =>
			userSession.webServiceHelper.callAsync<ISupportTicket>(SUPPORT_PATHS.tickets.detail(ticketId), 'GET'),
		enabled: !!ticketId,
		meta: { toastErrorMessage: 'Failed to load ticket' },
	});
}

/**
 * Warms the ticket detail + threads caches so opening a ticket renders instantly.
 * Wire the returned callback to row hover/focus in the inbox lists. The 10s
 * `staleTime` makes repeated hovers over the same row a no-op instead of a request
 * per mouse pass, and `prefetchQuery` never throws — a failed prefetch just falls
 * back to the normal on-select fetch.
 */
export function usePrefetchSupportTicket() {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return React.useCallback(
		(ticketId: string) => {
			if (!ticketId) return;
			void queryClient.prefetchQuery({
				queryKey: SUPPORT_QUERY_KEYS.tickets.detail(ticketId),
				staleTime: 10_000,
				queryFn: () =>
					userSession.webServiceHelper.callAsync<ISupportTicket>(SUPPORT_PATHS.tickets.detail(ticketId), 'GET'),
			});
			void queryClient.prefetchQuery({
				queryKey: SUPPORT_QUERY_KEYS.tickets.threads(ticketId),
				staleTime: 10_000,
				queryFn: () =>
					userSession.webServiceHelper.callAsync<IPagedCollection<ISupportThread>>(
						SUPPORT_PATHS.threads.list(ticketId),
						'GET'
					),
			});
		},
		[queryClient, userSession]
	);
}
